import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useWorkspace } from '../contexts/WorkspaceContext';
import api from '../lib/api';
import { Sparkles, Loader2, Send, FolderKanban, User, Users, Briefcase, Mic, MicOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const taskApi = { create: (data) => api.post('/tasks', data) };

// Fast client-side parser for common patterns — avoids LLM call for 90% of tasks
export const quickParse = (text) => {
  const lower = text.toLowerCase();
  let title = text.trim();
  let due_date = null;
  let due_time = null;
  let priority = 'medium';
  let labels = [];

  const today = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];

  // Priority detection
  if (/\b(urgent|asap|critical)\b/i.test(lower)) { priority = 'high'; title = title.replace(/\b(urgent|asap|critical)[:\s]*/i, '').trim(); }
  else if (/\bhigh\s*(?:priority|prio)?\b/i.test(lower)) { priority = 'high'; title = title.replace(/\bhigh\s*(?:priority|prio)?\b/i, '').trim(); }
  else if (/\blow\s*(?:priority|prio)?\b/i.test(lower)) { priority = 'low'; title = title.replace(/\blow\s*(?:priority|prio)?\b/i, '').trim(); }
  else if (/\bmedium\s*(?:priority|prio)?\b/i.test(lower)) { title = title.replace(/\bmedium\s*(?:priority|prio)?\b/i, '').trim(); }

  // Date detection
  if (/\btoday\b/i.test(lower)) { due_date = fmt(today); title = title.replace(/\b(by\s+)?today\b/i, '').trim(); }
  else if (/\btomorrow\b/i.test(lower)) { const d = new Date(today); d.setDate(d.getDate() + 1); due_date = fmt(d); title = title.replace(/\b(by\s+)?tomorrow\b/i, '').trim(); }
  else if (/\bnext\s+(mon|tue|wed|thu|fri|sat|sun)/i.test(lower)) {
    const dayMap = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
    const match = lower.match(/\bnext\s+(mon|tue|wed|thu|fri|sat|sun)\w*/i);
    if (match) {
      const target = dayMap[match[1].toLowerCase().slice(0, 3)];
      const d = new Date(today);
      let diff = target - d.getDay();
      if (diff <= 0) diff += 7;
      d.setDate(d.getDate() + diff);
      due_date = fmt(d);
      title = title.replace(/\b(by\s+)?next\s+\w+day?\b/i, '').trim();
    }
  }
  else {
    // Match explicit dates like "April 10", "4/10", "2026-04-10"
    const isoMatch = lower.match(/\b(\d{4}-\d{2}-\d{2})\b/);
    if (isoMatch) { due_date = isoMatch[1]; title = title.replace(isoMatch[0], '').trim(); }
  }

  // Time detection (e.g., "3pm", "15:00", "at 3:30pm")
  const timeMatch = lower.match(/\b(?:at\s+)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/i);
  if (timeMatch) {
    let h = parseInt(timeMatch[1]);
    const m = timeMatch[2] ? parseInt(timeMatch[2]) : 0;
    const ampm = timeMatch[3].toLowerCase();
    if (ampm === 'pm' && h < 12) h += 12;
    if (ampm === 'am' && h === 12) h = 0;
    due_time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    title = title.replace(timeMatch[0], '').trim();
  } else {
    const time24Match = lower.match(/\b(?:at\s+)?(\d{1,2}):(\d{2})\b/);
    if (time24Match) {
      due_time = `${String(time24Match[1]).padStart(2, '0')}:${time24Match[2]}`;
      title = title.replace(time24Match[0], '').trim();
    }
  }

  // Clean up leftover words
  title = title.replace(/\s{2,}/g, ' ').replace(/^[,\s]+|[,\s]+$/g, '').trim();
  if (!title) title = text.trim();

  return { title, description: null, due_date, due_time, priority, labels, is_recurring: false, recurrence_rule: null };
};

// Detect if input needs LLM (recurring rules, complex descriptions)
export const needsLLM = (text) => {
  const lower = text.toLowerCase();
  return /\b(every\s+(day|week|month|monday|tuesday|wednesday|thursday|friday|saturday|sunday))\b/i.test(lower) ||
    /\b(recurring|repeat|daily|weekly|monthly)\b/i.test(lower);
};

const AITaskBar = ({ onTaskCreated, defaultWorkspaceId = null, defaultProjectId = null }) => {
  const { workspaces } = useWorkspace();
  const { user } = useAuth();
  
  const [input, setInput] = useState('');
  const [workspaceId, setWorkspaceId] = useState(defaultWorkspaceId || '');
  const [isPersonalSelected, setIsPersonalSelected] = useState(!defaultWorkspaceId);
  const [projectId, setProjectId] = useState(defaultProjectId || '');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Smart voice state (Whisper-based)
  const [isRecording, setIsRecording] = useState(false);
  const [voiceProcessing, setVoiceProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const silenceTimerRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const micReadyRef = useRef(false);
  const mimeTypeRef = useRef('');
  const fileExtRef = useRef('webm');

  // Pre-warm microphone on mount for instant start
  useEffect(() => {
    const prewarm = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 } });
        streamRef.current = stream;
        // Determine best mime type once
        mimeTypeRef.current = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' :
                              MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' :
                              MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' :
                              MediaRecorder.isTypeSupported('audio/ogg') ? 'audio/ogg' : '';
        fileExtRef.current = mimeTypeRef.current.includes('mp4') ? 'mp4' : mimeTypeRef.current.includes('ogg') ? 'ogg' : 'webm';
        micReadyRef.current = true;
      } catch {
        // Permission denied or no mic — will handle on click
        micReadyRef.current = false;
      }
    };
    prewarm();
    return () => {
      if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
      if (audioContextRef.current) { audioContextRef.current.close().catch(() => {}); }
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    };
  }, []);

  // Load projects for personal workspace on initial mount
  useEffect(() => {
    if (isPersonalSelected && workspaces.length > 0) {
      const personalWs = workspaces.find(ws => ws.workspace_type === 'personal');
      if (personalWs) fetchProjects(personalWs.workspace_id);
    }
  }, [workspaces, isPersonalSelected]);

  const fetchProjects = async (wsId) => {
    if (!wsId) { setProjects([]); return; }
    setLoadingProjects(true);
    try {
      const response = await api.get(`/workspaces/${wsId}`);
      setProjects(response.data.projects || []);
    } catch { setProjects([]); }
    finally { setLoadingProjects(false); }
  };

  const handleWorkspaceChange = (value) => {
    if (value === 'personal') {
      setWorkspaceId('');
      setIsPersonalSelected(true);
      setProjectId('');
      const personalWs = workspaces.find(ws => ws.workspace_type === 'personal');
      if (personalWs) fetchProjects(personalWs.workspace_id);
      else setProjects([]);
    } else {
      setWorkspaceId(value);
      setIsPersonalSelected(false);
      setProjectId('');
      fetchProjects(value);
    }
  };

  const getResolvedTaskData = (parsed, titleFallback) => {
    const isPersonalTask = isPersonalSelected || !workspaceId;
    const personalWs = workspaces.find(ws => ws.workspace_type === 'personal');
    const today = new Date().toISOString().split('T')[0];
    return {
      title: parsed.title || titleFallback,
      description: parsed.description || null,
      due_date: parsed.due_date || (isPersonalTask ? today : null),
      due_time: parsed.due_time || null,
      priority: parsed.priority || 'medium',
      labels: parsed.labels || [],
      is_recurring: parsed.is_recurring || false,
      recurrence_rule: parsed.recurrence_rule || null,
      workspace_id: isPersonalTask ? (personalWs?.workspace_id || null) : (workspaceId || null),
      project_id: projectId || null,
      assigned_to: isPersonalTask ? (user?.user_id || null) : null,
      is_shared: !!workspaceId && !isPersonalTask
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setLoading(true);
    try {
      let parsed;
      if (needsLLM(input)) {
        const parseResponse = await api.post('/ai/parse-task', { text: input });
        parsed = parseResponse.data;
      } else {
        parsed = quickParse(input);
      }
      const taskData = getResolvedTaskData(parsed, input.trim());
      await taskApi.create(taskData);
      toast.success(`Task created: ${taskData.title}`);
      setInput('');
      onTaskCreated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    } finally { setLoading(false); }
  };

  // --- Smart Voice (Whisper) ---
  const stopRecording = useCallback(() => {
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    // Don't kill the stream — keep it pre-warmed for next recording
    if (audioContextRef.current) { audioContextRef.current.close().catch(() => {}); audioContextRef.current = null; }
    setIsRecording(false);
  }, []);

  const createTaskFromVoice = useCallback(async (audioBlob, ext = 'webm') => {
    setVoiceProcessing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, `voice.${ext}`);
      const transcribeRes = await api.post('/voice/transcribe', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const text = transcribeRes.data?.transcription || transcribeRes.data?.text || '';
      if (!text.trim()) { toast.error('No speech detected. Please try again.'); return; }

      setInput(text);
      const parseRes = await api.post('/ai/parse-task', { text });
      const taskData = getResolvedTaskData(parseRes.data, text.trim());
      await taskApi.create(taskData);
      toast.success(`Task created from voice: ${taskData.title}`);
      setInput('');
      onTaskCreated?.();
    } catch { toast.error('Voice task creation failed'); }
    finally { setVoiceProcessing(false); }
  }, [workspaces, user, workspaceId, isPersonalSelected, projectId, onTaskCreated]);

  const startRecording = useCallback(async () => {
    try {
      // Use pre-warmed stream or request new one
      let stream = streamRef.current;
      if (!stream || !stream.active) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 } });
        streamRef.current = stream;
        if (!mimeTypeRef.current) {
          mimeTypeRef.current = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' :
                                MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' :
                                MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '';
          fileExtRef.current = mimeTypeRef.current.includes('mp4') ? 'mp4' : 'webm';
        }
      }

      // Set recording state immediately for instant visual feedback
      setIsRecording(true);

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.5;
      source.connect(analyser);
      analyserRef.current = analyser;

      const recorderOptions = mimeTypeRef.current ? { mimeType: mimeTypeRef.current, audioBitsPerSecond: 128000 } : {};
      const mediaRecorder = new MediaRecorder(stream, recorderOptions);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeTypeRef.current || 'audio/webm' });
        if (audioBlob.size > 1000) createTaskFromVoice(audioBlob, fileExtRef.current);
        else { toast.error('Recording too short'); setVoiceProcessing(false); }
      };

      mediaRecorder.start(200);

      // Silence detection — auto-stop after 2.5s of silence, higher threshold for accuracy
      let silenceStart = null;
      let speechDetected = false;
      const SILENCE_THRESHOLD = 12;
      const SILENCE_DURATION = 2500;
      const MAX_WAIT_FOR_SPEECH = 8000;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const startTime = Date.now();

      const checkSilence = () => {
        if (!analyserRef.current || !mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') return;
        analyserRef.current.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;

        if (avg >= SILENCE_THRESHOLD) {
          speechDetected = true;
          silenceStart = null;
        } else {
          if (speechDetected) {
            // Only start silence timer after speech was detected
            if (!silenceStart) silenceStart = Date.now();
            else if (Date.now() - silenceStart > SILENCE_DURATION) { stopRecording(); return; }
          } else if (Date.now() - startTime > MAX_WAIT_FOR_SPEECH) {
            // No speech detected after 8s — stop
            stopRecording();
            return;
          }
        }
        silenceTimerRef.current = setTimeout(checkSilence, 80);
      };
      // Start silence detection after brief warm-up
      setTimeout(checkSilence, 500);

      // Safety: auto-stop after 30s max
      setTimeout(() => { if (mediaRecorderRef.current?.state === 'recording') stopRecording(); }, 30000);
    } catch { toast.error('Microphone access denied'); setIsRecording(false); }
  }, [createTaskFromVoice, stopRecording]);

  const toggleVoice = () => {
    if (isRecording) stopRecording();
    else startRecording();
  };

  const getWorkspaceIcon = (type) => {
    if (type === 'team') return <Users className="w-4 h-4" />;
    if (type === 'client') return <Briefcase className="w-4 h-4" />;
    return <User className="w-4 h-4" />;
  };

  return (
    <div className="bg-gradient-to-r from-[#4A9CC7]/5 to-blue-50 dark:from-[#4A9CC7]/10 dark:to-slate-800 rounded-xl border border-[#4A9CC7]/20 p-4" data-testid="ai-task-bar">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-[#4A9CC7] flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-800 dark:text-gray-100">AI Quick Task</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">Type or speak: "Review docs tomorrow 3pm high priority"</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="flex gap-2 items-center">
          <div className="flex-1 relative max-w-xl">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={voiceProcessing ? "Creating task from voice..." : "What needs to be done?"}
              className="pr-10 h-10"
              disabled={loading || voiceProcessing}
              data-testid="ai-task-input"
            />
            {(loading || voiceProcessing) && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#4A9CC7]" />
            )}
          </div>
          
          {/* Voice Input Button — Whisper-based, auto-create */}
          <Button
            type="button"
            variant={isRecording ? "default" : "outline"}
            onClick={toggleVoice}
            disabled={voiceProcessing}
            className={`h-10 w-10 p-0 flex-shrink-0 ${isRecording ? 'bg-red-500 hover:bg-red-600 animate-pulse' : 'hover:bg-[#4A9CC7]/10 hover:border-[#4A9CC7] dark:border-slate-600'}`}
            data-testid="voice-input-btn"
            title={voiceProcessing ? "Processing..." : isRecording ? "Stop recording" : "Voice input — auto-creates task"}
          >
            {voiceProcessing ? (
              <Loader2 className="w-4 h-4 animate-spin text-[#4A9CC7]" />
            ) : isRecording ? (
              <MicOff className="w-4 h-4 text-white" />
            ) : (
              <Mic className="w-4 h-4 text-[#4A9CC7]" />
            )}
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <Select value={workspaceId || 'personal'} onValueChange={handleWorkspaceChange}>
            <SelectTrigger className="w-[160px] h-9" data-testid="ai-workspace-select">
              <FolderKanban className="w-4 h-4 mr-2 text-gray-400" />
              <SelectValue placeholder="Workspace" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="personal">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Personal
                </div>
              </SelectItem>
              {workspaces.filter(ws => ws.workspace_type !== 'personal').map(ws => (
                <SelectItem key={ws.workspace_id} value={ws.workspace_id}>
                  <div className="flex items-center gap-2">
                    {getWorkspaceIcon(ws.workspace_type)}
                    {ws.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select 
            value={projectId || 'none'} 
            onValueChange={(v) => setProjectId(v === 'none' ? '' : v)}
            disabled={(!workspaceId && !isPersonalSelected) || loadingProjects}
          >
            <SelectTrigger className="w-[150px] h-9" data-testid="ai-project-select">
              <SelectValue placeholder={loadingProjects ? "Loading..." : "Project"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Project</SelectItem>
              {projects.map(proj => (
                <SelectItem key={proj.project_id} value={proj.project_id}>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: proj.color || '#4A9CC7' }} />
                    {proj.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button 
            type="submit" 
            disabled={loading || voiceProcessing || !input.trim()}
            className="bg-[#4A9CC7] hover:bg-[#3d8ab5] h-9"
            data-testid="ai-create-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Create
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AITaskBar;
